import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { fmtDate } from "@/lib/format";
import { StatusPill } from "@/components/StatusBanner";
import { useDebounced } from "@/lib/useDebounced";
import { toast } from "sonner";

export default function RegistrationSearch() {
  const [q, setQ] = useState("");
  const debouncedQ = useDebounced(q, 350);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (!debouncedQ.trim()) { setItems([]); setSearched(false); return; }
    let cancelled = false;
    setLoading(true); setSearched(true);
    api.get("/vehicles/search", { params: { q: debouncedQ.trim() } })
       .then(({ data }) => { if (!cancelled) setItems(data.items); })
       .catch((e) => { if (!cancelled) toast.error(formatError(e, "Search failed")); })
       .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [debouncedQ]);

  return (
    <div className="max-w-4xl">
      <div className="mb-6">
        <div className="label-overline">Operations</div>
        <h1 className="text-3xl font-black tracking-tight mt-1 text-slate-900">Registration Search</h1>
      </div>

      <div className="mb-4">
        <Input data-testid="reg-search-input"
               placeholder="Type registration (e.g. ABC…)"
               className="rounded h-12 font-mono text-lg uppercase tracking-widest"
               value={q} onChange={(e) => setQ(e.target.value.toUpperCase())} />
      </div>

      <div className="bg-white border border-slate-200 rounded overflow-x-auto">
        {loading && <div className="px-4 py-6 text-center text-slate-500">Searching…</div>}
        {!loading && searched && items.length === 0 && (
          <div className="px-4 py-8 text-center">
            <div className="label-overline text-slate-500">No matches</div>
            <p className="text-sm text-slate-600 mt-2">You can still open a new entry for this registration.</p>
            <div className="mt-3">
              <Button className="rounded bg-blue-700 hover:bg-blue-800"
                      data-testid="reg-open-new-entry"
                      onClick={() => nav("/app/new-entry")}>New Truck Entry →</Button>
            </div>
          </div>
        )}
        {items.length > 0 && (
          <table className="tbl w-full text-sm min-w-[720px]">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="label-overline text-slate-600">
                <th className="text-left px-4 py-3">Registration</th>
                <th className="text-left px-4 py-3">Customer</th>
                <th className="text-left px-4 py-3">Last order</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Last seen</th>
              </tr>
            </thead>
            <tbody>
              {items.map((v) => (
                <tr key={v.id} className="border-b border-slate-100">
                  <td className="px-4 py-3 font-mono font-bold text-blue-700">{v.registration_no}</td>
                  <td className="px-4 py-3">
                    {v.customer ? <span className="font-mono">{v.customer.customer_code} · {v.customer.display_name}</span>
                                : <span className="text-slate-500">Walk-in history</span>}
                  </td>
                  <td className="px-4 py-3">
                    {v.last_order ? <Link className="text-blue-700 font-mono" to={`/app/orders/${v.last_order.order_no}`}>{v.last_order.order_no}</Link> : "—"}
                  </td>
                  <td className="px-4 py-3">{v.last_order ? <StatusPill status={v.last_order.status} /> : "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{fmtDate(v.last_seen_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
